import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TourActionContext, TourDefinition, TourPlacement, TourStep } from './types';
import './feature-tour.css';

/**
 * TourOverlay — aktif turun görsel katmanı: karartma + hedef etrafında spot ışığı,
 * nabız halkası ve hedefe göre konumlanan ipucu kartı (Geri / İleri / Atla, nokta
 * ilerleme, klavye ← → Esc). Hedef hareket ederse (animasyonlu panel vb.) spot
 * ışığı her karede yeniden ölçülür.
 */

interface Props {
  tour: TourDefinition;
  stepIndex: number;
  ctx: TourActionContext;
  onStepChange: (step: number) => void;
  onFinish: () => void;
  onSkip: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const MARGIN = 12;
const CARD_GAP = 14;

const sameRect = (a: Rect | null, b: Rect | null) =>
  (!a && !b) || (!!a && !!b && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5);

const measure = (el: Element, pad: number): Rect => {
  const r = el.getBoundingClientRect();
  return { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
};

const findTarget = (step: TourStep): Element | null => {
  if (!step.target) return null;
  return document.querySelector(step.target) || (step.fallbackTarget ? document.querySelector(step.fallbackTarget) : null);
};

const pickPlacement = (rect: Rect, card: { w: number; h: number }, pref: TourPlacement = 'auto'): Exclude<TourPlacement, 'auto'> => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const space = {
    bottom: vh - (rect.top + rect.height),
    top: rect.top,
    right: vw - (rect.left + rect.width),
    left: rect.left,
  };
  const fits = {
    bottom: space.bottom >= card.h + CARD_GAP + MARGIN,
    top: space.top >= card.h + CARD_GAP + MARGIN,
    right: space.right >= card.w + CARD_GAP + MARGIN,
    left: space.left >= card.w + CARD_GAP + MARGIN,
  };
  if (pref !== 'auto' && fits[pref]) return pref;
  const order: Array<Exclude<TourPlacement, 'auto'>> = ['bottom', 'top', 'right', 'left'];
  const ok = order.find((p) => fits[p]);
  if (ok) return ok;
  // Hiçbiri sığmıyorsa en geniş boşluk
  return (Object.keys(space) as Array<Exclude<TourPlacement, 'auto'>>).sort((a, b) => space[b] - space[a])[0];
};

const TourOverlay: React.FC<Props> = ({ tour, stepIndex, ctx, onStepChange, onFinish, onSkip }) => {
  const step = tour.steps[stepIndex];
  const total = tour.steps.length;
  const isLast = stepIndex === total - 1;
  const [rect, setRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const [centered, setCentered] = useState(false);
  const [missingTarget, setMissingTarget] = useState(false);
  const targetRef = useRef<Element | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardSize, setCardSize] = useState({ w: 360, h: 200 });
  const runRef = useRef(0);
  // Adım değişiminde spot ışığı yayla kayar; ardından hedef hareket ederse (gösterimler)
  // anlık takip eder. Kart ise adım boyunca ilk ölçülen yerde sabit kalır (okunabilsin).
  const [following, setFollowing] = useState(false);
  const [anchor, setAnchor] = useState<Rect | null>(null);
  const pad = step?.spotlightPadding ?? 8;

  // Adım hazırlığı: onBefore → hedefi bekle → görünür alana kaydır → ölç → onEnter
  useEffect(() => {
    const run = ++runRef.current;
    const alive = () => run === runRef.current;
    setReady(false);
    setFollowing(false);
    setAnchor(null);
    setMissingTarget(false);
    (async () => {
      try {
        await step.onBefore?.(ctx);
      } catch {
        /* hazırlık hatası turu durdurmasın */
      }
      if (!alive()) return;
      if (!step.target) {
        targetRef.current = null;
        setRect(null);
        setCentered(true);
        setReady(true);
        step.onEnter?.(ctx);
        return;
      }
      const deadline = Date.now() + (step.waitForTargetMs ?? 3000);
      let el: Element | null = null;
      while (!el && Date.now() < deadline && alive()) {
        el = findTarget(step);
        if (!el) await ctx.sleep(80);
      }
      if (!alive()) return;
      if (!el) {
        // Hedef bu sayfada yok: adımı atlamak yerine ortada göster ve uyar (önizlemede seçici hatası anlaşılsın)
        targetRef.current = null;
        setRect(null);
        setCentered(true);
        setMissingTarget(true);
        setReady(true);
        return;
      }
      targetRef.current = el;
      const r = el.getBoundingClientRect();
      const visible = r.top >= 0 && r.bottom <= window.innerHeight;
      if (!visible) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        await ctx.sleep(350);
      }
      if (!alive()) return;
      setCentered(false);
      const first = measure(el, pad);
      setRect(first);
      setAnchor(first);
      setReady(true);
      window.setTimeout(() => alive() && setFollowing(true), 400);
      try {
        await step.onEnter?.(ctx);
      } catch {
        /* demo hatası turu durdurmasın */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id, stepIndex]);

  // Pencere boyu değişince kart çapasını tazele
  useEffect(() => {
    const onResize = () => {
      const el = targetRef.current;
      if (el && document.contains(el)) setAnchor(measure(el, pad));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pad]);

  // Hedef hareket ederse spot ışığını takip et
  useEffect(() => {
    if (!ready || centered) return;
    let raf = 0;
    const tick = () => {
      const el = targetRef.current;
      if (el && document.contains(el)) {
        const next = measure(el, pad);
        setRect((prev) => (sameRect(prev, next) ? prev : next));
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [ready, centered, pad]);

  // Kart boyutu (yerleşim için)
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (Math.abs(r.width - cardSize.w) > 1 || Math.abs(r.height - cardSize.h) > 1) setCardSize({ w: r.width, h: r.height });
  });

  const goNext = useCallback(() => {
    if (isLast) onFinish();
    else onStepChange(stepIndex + 1);
  }, [isLast, onFinish, onStepChange, stepIndex]);
  const goPrev = useCallback(() => {
    if (stepIndex > 0) onStepChange(stepIndex - 1);
  }, [onStepChange, stepIndex]);

  // Klavye
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onSkip();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [goNext, goPrev, onSkip]);

  // Kart konumu
  const cardStyle = useMemo<React.CSSProperties>(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(cardSize.w, vw - MARGIN * 2);
    const h = cardSize.h;
    const base = anchor || rect;
    if (!base || centered) {
      return { left: Math.max(MARGIN, (vw - w) / 2), top: Math.max(MARGIN, (vh - h) / 2) };
    }
    const placement = pickPlacement(base, { w, h }, step.placement);
    let left = 0;
    let top = 0;
    if (placement === 'bottom' || placement === 'top') {
      left = base.left + base.width / 2 - w / 2;
      top = placement === 'bottom' ? base.top + base.height + CARD_GAP : base.top - h - CARD_GAP;
    } else {
      top = base.top + base.height / 2 - h / 2;
      left = placement === 'right' ? base.left + base.width + CARD_GAP : base.left - w - CARD_GAP;
    }
    left = Math.min(Math.max(MARGIN, left), vw - w - MARGIN);
    top = Math.min(Math.max(MARGIN, top), vh - h - MARGIN);
    return { left, top };
  }, [rect, anchor, centered, cardSize, step.placement]);

  const placementClass = useMemo(() => {
    const base = anchor || rect;
    if (!base || centered) return 'centered';
    return pickPlacement(base, { w: Math.min(cardSize.w, window.innerWidth - MARGIN * 2), h: cardSize.h }, step.placement);
  }, [rect, anchor, centered, cardSize, step.placement]);

  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const hole = rect && !centered ? rect : null;
  const dim = hole
    ? {
        top: { top: 0, left: 0, width: vw, height: Math.max(0, hole.top) },
        bottom: { top: hole.top + hole.height, left: 0, width: vw, height: Math.max(0, vh - hole.top - hole.height) },
        left: { top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
        right: { top: hole.top, left: hole.left + hole.width, width: Math.max(0, vw - hole.left - hole.width), height: hole.height },
      }
    : null;

  const motionTransition = { type: 'spring' as const, stiffness: 420, damping: 40 };
  const holeStyle = hole ? { top: hole.top, left: hole.left, width: hole.width, height: hole.height } : undefined;

  const stop = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="ftour-root" role="dialog" aria-modal="true" aria-label={`${tour.title} rehberi`}>
      {/* Karartma: hedef etrafında dört parça; hedef yoksa tam ekran */}
      {dim ? (
        (['top', 'bottom', 'left', 'right'] as const).map((k) =>
          following ? (
            // Takip modu: hedef hareket ederken (gösterimler) konum doğrudan yazılır, gecikme olmaz
            <div key={`${k}-f`} className="ftour-dim" style={dim[k]} onClick={stop} onPointerDown={stop} />
          ) : (
            <motion.div key={k} className="ftour-dim" initial={false} animate={dim[k]} transition={motionTransition} onClick={stop} onPointerDown={stop} />
          ),
        )
      ) : (
        <div className="ftour-dim ftour-dim-full" onClick={stop} onPointerDown={stop} />
      )}

      {/* Spot ışığı halkası + (etkileşim kapalıysa) tıklama kalkanı */}
      {hole && (
        <>
          {following ? <div className="ftour-ring" style={holeStyle} /> : <motion.div className="ftour-ring" initial={false} animate={holeStyle} transition={motionTransition} />}
          {!step.allowInteraction &&
            (following ? (
              <div className="ftour-shield" style={holeStyle} onClick={stop} onPointerDown={stop} />
            ) : (
              <motion.div className="ftour-shield" initial={false} animate={holeStyle} transition={motionTransition} onClick={stop} onPointerDown={stop} />
            ))}
        </>
      )}

      {/* İpucu kartı */}
      <AnimatePresence mode="wait">
        {ready && (
          <motion.div
            key={step.id}
            ref={cardRef}
            className={`ftour-card ${placementClass}`}
            style={cardStyle}
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            onClick={stop}
            onPointerDown={stop}
          >
            <span className="ftour-arrow" aria-hidden="true" />
            <div className="ftour-card-head">
              <span className="ftour-chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" />
                </svg>
                Rehber · {tour.title}
              </span>
              <button type="button" className="ftour-skip" onClick={onSkip}>
                Atla
              </button>
            </div>
            <div className="ftour-title">{step.title}</div>
            <div className="ftour-content">{step.content}</div>
            {missingTarget && (
              <div className="ftour-warn">
                Bu adımın hedef öğesi bu sayfada bulunamadı{step.target ? <code> {step.target}</code> : ''}. Rehber editöründen "Sayfada seç" ile yeniden seçin.
              </div>
            )}
            <div className="ftour-foot">
              <div className="ftour-dots" aria-label={`Adım ${stepIndex + 1} / ${total}`}>
                {tour.steps.map((s, i) => (
                  <i key={s.id} className={i === stepIndex ? 'active' : i < stepIndex ? 'done' : ''} />
                ))}
                <span className="ftour-count">
                  {stepIndex + 1} / {total}
                </span>
              </div>
              <div className="ftour-btns">
                {stepIndex > 0 && (
                  <button type="button" className="ftour-btn ghost" onClick={goPrev}>
                    Geri
                  </button>
                )}
                <button type="button" className="ftour-btn primary" onClick={goNext} autoFocus>
                  {isLast ? 'Bitir' : 'İleri'}
                  {!isLast && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TourOverlay;
